/**
 * ScrapingToolsSection - Controles de HypeStat, Fallback e Prioridades
 *
 * HypeStat agrega dados de 3 fontes: HypeStat, SEMrush e SimilarWeb.
 * Seleciona automaticamente o MAIOR valor de audiência entre as fontes.
 */
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Tooltip,
  Typography,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Collapse
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Assessment as AssessmentIcon,
  OpenInBrowser as OpenBrowserIcon,
  Close as CloseIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Sync as SyncIcon,
  BarChart as BarChartIcon,
  TuneRounded as TuneIcon,
  MergeType as MergeTypeIcon,
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';
import MensureLoader from '../../../atoms/MensureLoader';

const ScrapingToolsSection = ({
  hypestat,
  fallback,
  domains,
  onUpdatePercentile,
  onRecalculateFallback,
  onRecalculatePriorities,
  priorityRecalcLoading,
  onDedup,
  enrichProgress
}) => {
  const theme = useTheme();
  const [dedupDialog, setDedupDialog] = useState({ open: false, loading: false, preview: null, error: null });
  const [toolsOpen, setToolsOpen] = useState(false);

  const toolsTop = {
    xs: 'calc(var(--admin-appbar-height, 64px) + 292px)',
    sm: 'calc(var(--admin-appbar-height, 64px) + 238px)',
    md: 'calc(var(--admin-appbar-height, 64px) + 218px)',
    xl: 'calc(var(--admin-appbar-height, 64px) + 176px)'
  };

  const toolsMaxHeight = {
    xs: 'calc(100vh - (var(--admin-appbar-height, 64px) + 310px))',
    sm: 'calc(100vh - (var(--admin-appbar-height, 64px) + 256px))',
    md: 'calc(100vh - (var(--admin-appbar-height, 64px) + 236px))',
    xl: 'calc(100vh - (var(--admin-appbar-height, 64px) + 194px))'
  };

  const pinnedCardSx = {
    position: 'fixed',
    top: toolsTop,
    left: { xs: 12, md: 'calc(var(--admin-sidebar-width, 240px) + 24px)' },
    right: { xs: 12, md: 24 },
    zIndex: theme.zIndex.appBar - 1,
    mb: 0,
    border: '1px solid',
    borderColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.08)
      : alpha(theme.palette.common.black, 0.08),
    borderRadius: 2,
    background: theme.palette.mode === 'dark'
      ? theme.palette.background.paper
      : theme.palette.background.paper,
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
    maxHeight: toolsOpen ? toolsMaxHeight : { xs: 132, sm: 96, md: 86 },
    overflowY: toolsOpen ? 'auto' : 'hidden',
    overflowX: 'hidden',
    transition: 'max-height 180ms ease, box-shadow 180ms ease',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 14px 34px rgba(0, 0, 0, 0.26)'
      : '0 14px 34px rgba(15, 23, 42, 0.08)'
  };

  // === DEDUP ===
  const handleDedupPreview = async () => {
    setDedupDialog({ open: true, loading: true, preview: null, error: null });
    try {
      const res = await onDedup(true); // dryRun
      setDedupDialog({ open: true, loading: false, preview: res.data, error: null });
    } catch (e) {
      setDedupDialog({ open: true, loading: false, preview: null, error: e.message });
    }
  };

  const handleDedupExecute = async () => {
    setDedupDialog(d => ({ ...d, loading: true }));
    try {
      const res = await onDedup(false);
      setDedupDialog({ open: false, loading: false, preview: null, error: null });
      if (res.data?.removed > 0) alert(`✅ ${res.data.removed} duplicatas removidas com sucesso!`);
      else alert('Nenhuma duplicata encontrada.');
    } catch (e) {
      setDedupDialog(d => ({ ...d, loading: false, error: e.message }));
    }
  };

  if (!hypestat || !hypestat.initialized) {
    return (
      <Card elevation={0} sx={pinnedCardSx}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Carregando ferramentas de scraping...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const selectedCount = hypestat.selectedDomains?.length > 0
    ? hypestat.selectedDomains.length
    : domains.length;

  // Calcula % do enriquecimento
  const enrichPct = enrichProgress?.total > 0
    ? Math.round((enrichProgress.current / enrichProgress.total) * 100)
    : 0;
  const isEnriching = enrichProgress?.isRunning;
  const enrichDone = enrichProgress?.status === 'done' && enrichProgress?.total > 0;

  return (
    <>
      <Card elevation={0} sx={pinnedCardSx}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box
            display="flex"
            alignItems="center"
            gap={1.25}
            flexWrap={{ xs: 'wrap', md: 'nowrap' }}
            sx={{
              minWidth: 0,
              '& .MuiButton-root, & .MuiChip-root': {
                flexShrink: 0
              }
            }}
          >
            {/* Status HypeStat */}
            <Tooltip title="HypeStat agrega dados de HypeStat, SEMrush e SimilarWeb. Seleciona o maior valor automaticamente.">
              <Chip
                icon={<AssessmentIcon />}
                label={hypestat.browserOpen ? 'HypeStat Pronto' : 'Browser Fechado'}
                color={hypestat.browserOpen ? 'success' : 'default'}
                size="small"
              />
            </Tooltip>

            {/* Botão Abrir/Fechar Browser */}
            {!hypestat.browserOpen ? (
              <Button
                variant="outlined"
                startIcon={hypestat.loading ? <CircularProgress size={16} /> : <OpenBrowserIcon />}
                onClick={hypestat.openBrowser}
                disabled={hypestat.loading || hypestat.status.isRunning}
                size="small"
              >
                Abrir Browser
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<CloseIcon />}
                onClick={hypestat.closeBrowser}
                disabled={hypestat.loading || hypestat.status.isRunning}
                size="small"
              >
                Fechar Browser
              </Button>
            )}

            {/* Botão Iniciar Scraping */}
            <Button
              variant="contained"
              color="primary"
              startIcon={hypestat.loading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
              onClick={() => hypestat.startScraping(hypestat.selectedDomains, domains)}
              disabled={!hypestat.browserOpen || hypestat.loading || hypestat.status.isRunning || domains.length === 0}
              size="small"
            >
              Iniciar Scraping ({selectedCount})
            </Button>

            {/* Botão Parar */}
            {hypestat.status.isRunning && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<StopIcon />}
                onClick={hypestat.stopScraping}
                size="small"
              >
                Parar
              </Button>
            )}

            {/* Spacer */}
            <Box sx={{ flexGrow: 1 }} />

            <Button
              variant={toolsOpen ? 'outlined' : 'contained'}
              color="inherit"
              size="small"
              startIcon={<TuneIcon />}
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    transform: toolsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: '0.2s',
                    fontSize: 17
                  }}
                />
              }
              onClick={() => setToolsOpen((current) => !current)}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 1.5,
                color: toolsOpen ? 'text.primary' : 'background.default',
                bgcolor: toolsOpen ? 'transparent' : 'text.primary',
                '&:hover': {
                  bgcolor: toolsOpen
                    ? alpha(theme.palette.text.primary, 0.08)
                    : alpha(theme.palette.text.primary, 0.86)
                }
              }}
            >
              {toolsOpen ? 'Guardar ferramentas' : 'Abrir ferramentas'}
            </Button>
          </Box>

          <Collapse in={toolsOpen} timeout="auto" unmountOnExit>
          <Box display="flex" alignItems="center" gap={1.25} flexWrap="wrap" sx={{ mt: 1.5 }}>

            {/* Fallback Config */}
            {fallback.config && (
              <>
                <Chip
                  icon={<TuneIcon />}
                  label={`Fallback P${fallback.config.percentile}: ${fallback.config.monthlyVisits?.toLocaleString('pt-BR')} visitas/mês`}
                  color="default"
                  variant="outlined"
                  size="small"
                  onClick={() => fallback.setDialogOpen(true)}
                  sx={{ cursor: 'pointer' }}
                />
                <Button
                  variant="outlined"
                  startIcon={<SyncIcon />}
                  size="small"
                  onClick={onRecalculateFallback}
                  disabled={fallback.loading}
                >
                  Recalcular Fallback
                </Button>
              </>
            )}
            {fallback.loading && <CircularProgress size={20} />}

            <Tooltip title="Recalcula prioridades baseado em Tier e uso recente (ultimos 30 dias)">
              <Button
                variant="outlined"
                startIcon={priorityRecalcLoading ? <CircularProgress size={16} /> : <BarChartIcon />}
                size="small"
                onClick={onRecalculatePriorities}
                disabled={priorityRecalcLoading}
              >
                Recalc. Prioridades
              </Button>
            </Tooltip>

            <Tooltip title="Detecta e remove PendingDomains duplicados (mesmo registrableDomain)">
              <Button
                variant="outlined"
                color="warning"
                startIcon={<MergeTypeIcon />}
                size="small"
                onClick={handleDedupPreview}
              >
                Deduplicar
              </Button>
            </Tooltip>
          </Box>

          {/* Resultado do Enriquecimento */}
          {enrichDone && (
            <Alert severity={enrichProgress.errorCount > 0 ? "warning" : "success"} sx={{ mb: 2 }}>
              Enriquecimento concluído! <strong>{enrichProgress.successCount}</strong> sucessos, <strong>{enrichProgress.errorCount}</strong> erros.
            </Alert>
          )}

          {/* Progresso do Scraping (HypeStat) */}
          {hypestat.status.isRunning && (
            <Box mt={1.5} display="flex" alignItems="center" gap={1}>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 200 }}>
                {hypestat.status.progress?.domain} ({hypestat.status.progress?.current}/{hypestat.status.progress?.total})
              </Typography>
              <Box sx={{
                flexGrow: 1,
                height: 6,
                borderRadius: 3,
                bgcolor: 'action.disabledBackground',
                overflow: 'hidden'
              }}>
                <Box sx={{
                  height: '100%',
                  width: `${(hypestat.status.progress?.current / hypestat.status.progress?.total) * 100 || 0}%`,
                  bgcolor: 'primary.main',
                  transition: 'width 0.3s'
                }} />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {Math.round((hypestat.status.progress?.current / hypestat.status.progress?.total) * 100 || 0)}%
              </Typography>
            </Box>
          )}

          {/* Indicador de fonte multi-dados */}
          {hypestat.browserOpen && !hypestat.status.isRunning && (
            <Box mt={1} display="flex" gap={0.5}>
              <Chip label="HypeStat" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
              <Chip label="SEMrush" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
              <Chip label="SimilarWeb" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1, alignSelf: 'center' }}>
                Usa o maior valor entre as fontes
              </Typography>
            </Box>
          )}
          </Collapse>
        </CardContent>
      </Card>

      {/* Dialog de confirmação de Deduplicação (Padronizado) */}
      {isEnriching && (
        <MensureLoader
          fullScreen
          size="lg"
          message={`Enriquecendo com IA... ${enrichPct}% (${enrichProgress.current}/${enrichProgress.total}) - ${enrichProgress.domain}`}
        />
      )}

      <PlatformDialog 
        open={dedupDialog.open} 
        onClose={() => !dedupDialog.loading && setDedupDialog(d => ({ ...d, open: false }))} 
        maxWidth="sm" 
        fullWidth
      >
        <PlatformDialogTitle
          title="Deduplicar Domínios"
          subtitle="Remover registros repetidos na fila de pendências."
          icon={<MergeTypeIcon />}
          onClose={() => !dedupDialog.loading && setDedupDialog(d => ({ ...d, open: false }))}
          color="warning"
        />
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {dedupDialog.loading && (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={4}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary">Analisando base de dados...</Typography>
            </Box>
          )}
          {dedupDialog.error && <Alert severity="error" sx={{ borderRadius: 2 }}>{dedupDialog.error}</Alert>}
          {dedupDialog.preview && !dedupDialog.loading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {dedupDialog.preview.duplicates === 0 ? (
                <Alert severity="success" variant="filled" sx={{ borderRadius: 2 }}>
                  Nenhum domínio duplicado encontrado! A base está íntegra.
                </Alert>
              ) : (
                <>
                  <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
                    Encontrados <strong>{dedupDialog.preview.duplicates}</strong> grupos duplicados.
                    <br />
                    <strong>{dedupDialog.preview.removed}</strong> registros seriam removidos.
                  </Alert>

                  <Typography variant="subtitle2" color="text.secondary">
                    Relatório de Otimização:
                  </Typography>

                  <Box sx={{ 
                    maxHeight: 280, 
                    overflowY: 'auto', 
                    borderRadius: 2, 
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'action.hover'
                  }}>
                    {dedupDialog.preview.report?.map((r, i) => (
                      <Box key={i} p={1.5} sx={{ 
                        borderBottom: i < dedupDialog.preview.report.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                        '&:hover': { bgcolor: 'action.selected' }
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" fontWeight={700} color="primary">
                            {r.domain}
                          </Typography>
                          <Chip label={`${r.count}x`} size="small" sx={{ height: 16, fontSize: '0.6rem' }} />
                        </Box>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CheckIcon sx={{ fontSize: 12, color: 'success.main' }} />
                            <Typography variant="caption" color="text.secondary">
                              <strong>Manter:</strong> {r.kept?.status} · {r.kept?.audienceMonthly?.toLocaleString('pt-BR')} visitas
                            </Typography>
                          </Box>
                          {r.removed?.map((rm, j) => (
                            <Box key={j} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CloseIcon sx={{ fontSize: 12, color: 'error.main' }} />
                              <Typography variant="caption" color="text.disabled">
                                <strong>Remover:</strong> {rm.status} · {rm.audienceMonthly?.toLocaleString('pt-BR')} visitas
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button 
            onClick={() => setDedupDialog(d => ({ ...d, open: false }))} 
            disabled={dedupDialog.loading}
            sx={{ fontWeight: 600 }}
          >
            Cancelar
          </Button>
          {dedupDialog.preview?.duplicates > 0 && (
            <Button
              variant="contained"
              color="warning"
              onClick={handleDedupExecute}
              disabled={dedupDialog.loading}
              startIcon={dedupDialog.loading ? <CircularProgress size={16} color="inherit" /> : <MergeTypeIcon />}
              sx={{ fontWeight: 700, borderRadius: 2, px: 3, boxShadow: 'none' }}
            >
              Executar Limpeza
            </Button>
          )}
        </DialogActions>
      </PlatformDialog>
    </>);
};

export default ScrapingToolsSection;
